import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { SimilarCards, Button } from 'components';
import RedCircleYellowPoint from 'images/shared/misc/red_circle_yellow_point.svg';
import PurpleCircle from 'images/shared/misc/purple_circle.svg';
import PurpleBluePoints from 'images/shared/misc/purple_blue_points.svg';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ActivitiesAvailable = ({
  introduction,
  cards,
  linkPath,
  pathname,
  viewportWidth,
  buttonTitle
}) => {
  const { summary, title } = introduction;
  const [cardsToShow, setCardsToShow] = useState(cards.edges.slice(0, 6));
  const [showAll, setShowAll] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const toggleShowAll = (e) => {
    e.preventDefault();
    setIsAnimating(true);
    setShowAll(!showAll);
  };

  useEffect(() => {
    setCardsToShow(showAll ? cards.edges : cards.edges.slice(0, 6));
    // Reset animation state after cards are updated
    const timer = setTimeout(() => {
      setIsAnimating(false);
    }, 300); // Match this with the animation duration
    return () => clearTimeout(timer);
  }, [showAll]);
  return (
    <Container>
      {viewportWidth > 971 && (
        <>
          <RedCircleYellowPointSVG src={RedCircleYellowPoint} />
          <PurpleCircleSVG src={PurpleCircle} />
          <PurpleBluePointsSVG src={PurpleBluePoints} />
        </>
      )}
      <Content>
        <Introduction>
          <h2>{title}</h2>
          <p>{summary}</p>
        </Introduction>
        <CardContainer isAnimating={isAnimating}>
          <SimilarCards
            similarCards={cardsToShow}
            linkPath={linkPath}
            pathname={pathname}
            hideTitle
            hideBlob
            viewportWidth={viewportWidth}
            hideButton
          />
        </CardContainer>
        {cards.edges.length > 6 && (
          <Button
            handleClick={toggleShowAll}
            buttonColor="--dynamic-blue"
            buttonHoverColor="--dynamic-blue"
            fontColor="--dynamic-white"
            boxShadow="0px 4px 4px rgba(0, 0, 0, 0.15)"
            text={showAll ? 'Show Less' : 'Show All'}
          />
        )}
      </Content>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  margin: 3rem 0;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  justify-content: center;
  max-width: 1145px;
  width: 100%;
  padding: 0 1.5rem;
  @media (min-width: 1024px) {
    padding: 0;
  }
`;

const Introduction = styled.div`
  max-width: 914px;
  margin: 3rem auto 0 auto;
  padding: 0 1.5rem;

  h2 {
    color: var(--dynamic-blue);
    font-family: adelle, serif;
    font-size: 1.9rem;
    font-weight: 600;
    text-align: center;
    margin-bottom: 2rem;

    @media screen and (min-width: 1143px) {
      font-size: 2rem;
    }
  }

  p {
    color: var(--dynamic-dark-grey);
    font-family: 'proxima-nova', sans-serif;
    font-size: 1.125rem;
    font-weight: 400;
    font-style: normal;
    letter-spacing: 0.01em;
    margin-bottom: 1.75rem;
    text-align: center;
    a {
      color: var(--dynamic-blue);
      text-decoration: underline;
      font-weight: 600;
    }
  }
  @media screen and (min-width: 1200px) {
    font-size: 1.3rem;
    margin: 6rem auto 0 auto;
    padding: 0;
  }
  @media screen and (min-width: 1600px) {
    margin: 0 auto;
  }
`;

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
  gap: 2rem;
  section {
    width: 100%;
    animation: ${props => props.isAnimating ? fadeIn : 'none'} 0.3s ease-out;
  }
`;

const RedCircleYellowPointSVG = styled.img`
  position: absolute;
  bottom: -100px;
  right: 0;
  width: auto;
  height: 298px;
  z-index: 1;
`;

const PurpleCircleSVG = styled.img`
  position: absolute;
  top: 150%;
  left: 0;
  width: auto;
  height: 160px;
`;

const PurpleBluePointsSVG = styled.img`
  position: absolute;
  top: 20%;
  left: 0;
  width: auto;
  height: 220px;
`;

export default ActivitiesAvailable;
